// =============================================
// PANEL DE NOTIFICACIONES DE WHATSAPP
// Control de envío de mensajes por WhatsApp
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Checkbox,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  QrCode as QrCodeIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarIcon,
  Message as MessageIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import whatsappService from '../../services/whatsappService';
import userService from '../../services/userService';

const WhatsAppNotifications = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [qrDialog, setQrDialog] = useState(false);
  const [qrCode, setQrCode] = useState('');
  
  // Dialog de nuevo período
  const [periodDialog, setPeriodDialog] = useState(false);
  const [periodData, setPeriodData] = useState({ 
    year: new Date().getFullYear(), 
    admin: '', 
    adminId: null 
  });
  
  // Dialog de mensaje personalizado
  const [customDialog, setCustomDialog] = useState(false);
  const [customMessage, setCustomMessage] = useState({
    phone: '',
    message: '',
    ownerName: ''
  });

  const [owners, setOwners] = useState([]);
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [selectedOwners, setSelectedOwners] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Cargar estado de WhatsApp
  const loadWhatsAppStatus = async () => {
    try {
      const response = await whatsappService.getStatus();
      setWhatsappStatus(response.status);
    } catch (err) {
      console.error('Error al cargar estado de WhatsApp:', err);
    }
  };

  useEffect(() => {
    loadWhatsAppStatus();
    const interval = setInterval(loadWhatsAppStatus, 10000); // Cada 10 segundos
    return () => clearInterval(interval);
  }, []);

  // Mostrar QR code
  const handleShowQR = async () => {
    try {
      setLoading(true);
      const response = await whatsappService.getQR();
      if (response.success && response.qr) {
        setQrCode(response.qr);
        setQrDialog(true);
      } else {
        setError(response.message || 'No hay QR disponible');
      }
    } catch (err) {
      setError('Error al obtener QR');
    } finally {
      setLoading(false);
    }
  };

  // Enviar recordatorios de deuda
  const handleDebtReminders = async () => {
    if (!window.confirm('¿Enviar recordatorios por WhatsApp a todos los morosos?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setResults(null);
      const response = await whatsappService.sendDebtReminders();
      setResults(response);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al enviar recordatorios');
    } finally {
      setLoading(false);
    }
  };

  // Cargar propietarios
  const loadOwners = async () => {
    try {
      setLoadingOwners(true);
      const response = await userService.getOwners();
      const ownersWithPhone = response.users.filter(u => u.phone);
      setOwners(ownersWithPhone);
      setSelectedOwners([]);
      setSelectAll(false);
    } catch (err) {
      setError('Error al cargar propietarios');
      console.error(err);
    } finally {
      setLoadingOwners(false);
    }
  };

  // Seleccionar todos
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedOwners(owners.map(o => o.id));
    } else {
      setSelectedOwners([]);
    }
  };

  // Toggle propietario
  const handleToggleOwner = (ownerId) => {
    const currentIndex = selectedOwners.indexOf(ownerId);
    const newSelected = [...selectedOwners];

    if (currentIndex === -1) {
      newSelected.push(ownerId);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelectedOwners(newSelected);
    setSelectAll(newSelected.length === owners.length);
  };

  // Abrir dialog de nuevo período
  const handleOpenPeriodDialog = async () => {
    setPeriodDialog(true);
    setError('');
    await loadOwners();
  };

  // Enviar nuevo período
  const handleNewPeriod = async () => {
    if (!periodData.year || !periodData.adminId) {
      setError('Complete todos los campos');
      return;
    }

    if (selectedOwners.length === 0) {
      setError('Seleccione al menos un propietario');
      return;
    }

    const count = selectedOwners.length;

    if (!window.confirm(`¿Notificar nuevo período ${periodData.year} por WhatsApp a ${count} propietario${count !== 1 ? 's' : ''}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setResults(null);
      
      const response = await whatsappService.sendNewPeriodNotification(
        periodData.year,
        periodData.admin,
        selectedOwners
      );
      
      setResults(response);
      setPeriodDialog(false);
      setPeriodData({ year: new Date().getFullYear(), admin: '', adminId: null });
      setSelectAll(false);
      setSelectedOwners([]);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al enviar notificación');
    } finally {
      setLoading(false);
    }
  };

  // Enviar mensaje personalizado
  const handleCustomMessage = async () => {
    if (!customMessage.phone || !customMessage.message) {
      setError('Complete teléfono y mensaje');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setResults(null);
      
      const response = await whatsappService.sendCustomMessage(
        customMessage.phone,
        customMessage.message,
        customMessage.ownerName
      );
      
      if (response.success) {
        setResults({
          success: true,
          sent: 1,
          failed: 0,
          results: [{
            phone: customMessage.phone,
            status: 'sent',
            message: 'Mensaje enviado'
          }]
        });
        setCustomDialog(false);
        setCustomMessage({ phone: '', message: '', ownerName: '' });
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al enviar mensaje');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <WhatsAppIcon sx={{ fontSize: 40, color: '#25D366', mr: 2 }} />
        <Typography variant="h4">
          Notificaciones WhatsApp
        </Typography>
      </Box>

      {/* Estado de conexión */}
      <Alert 
        severity={whatsappStatus?.connected ? 'success' : 'warning'} 
        sx={{ mb: 3 }}
        action={
          <>
            <Button 
              color="inherit" 
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadWhatsAppStatus}
            >
              Actualizar
            </Button>
            {!whatsappStatus?.connected && (
              <Button 
                color="inherit" 
                size="small"
                startIcon={<QrCodeIcon />}
                onClick={handleShowQR}
                disabled={loading}
              >
                Ver QR
              </Button>
            )}
          </>
        }
      >
        <strong>Estado:</strong> {whatsappStatus?.connected ? '✅ Conectado' : '⚠️ Desconectado - Escanea el QR'}
      </Alert>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Resultados */}
      {results && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          onClose={() => setResults(null)}
        >
          <Typography variant="body2" gutterBottom>
            <strong>Enviados:</strong> {results.sent} | <strong>Fallidos:</strong> {results.failed}
          </Typography>
          {results.results && results.results.length > 0 && (
            <List dense>
              {results.results.slice(0, 5).map((result, index) => (
                <ListItem key={index}>
                  <Chip
                    icon={result.status === 'sent' ? <CheckIcon /> : <ErrorIcon />}
                    label={result.status === 'sent' ? 'Enviado' : 'Error'}
                    color={result.status === 'sent' ? 'success' : 'error'}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <ListItemText 
                    primary={result.owner || result.phone}
                    secondary={result.message}
                  />
                </ListItem>
              ))}
              {results.results.length > 5 && (
                <Typography variant="caption" sx={{ ml: 2 }}>
                  ... y {results.results.length - 5} más
                </Typography>
              )}
            </List>
          )}
        </Alert>
      )}

      {/* Tarjetas de acciones */}
      <Grid container spacing={3}>
        {/* Recordatorios de Deuda */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Recordatorios de Deuda
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Envía recordatorios automáticos por WhatsApp a todos los propietarios con pagos pendientes.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={handleDebtReminders}
                disabled={loading || !whatsappStatus?.connected}
                startIcon={loading ? <CircularProgress size={20} /> : <WhatsAppIcon />}
              >
                {loading ? 'Enviando...' : 'Enviar Recordatorios'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Nuevo Período */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Notificar Nuevo Período
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Notifica a los propietarios sobre el inicio de un nuevo período administrativo.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="outlined"
                color="info"
                fullWidth
                onClick={handleOpenPeriodDialog}
                disabled={loading || !whatsappStatus?.connected}
                startIcon={<CalendarIcon />}
              >
                Notificar Período
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Mensaje Personalizado */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MessageIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Mensaje Personalizado
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Envía un mensaje personalizado a un número específico.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                onClick={() => setCustomDialog(true)}
                disabled={loading || !whatsappStatus?.connected}
                startIcon={<MessageIcon />}
              >
                Enviar Mensaje
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog: QR Code */}
      <Dialog open={qrDialog} onClose={() => setQrDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>📱 Escanear QR con WhatsApp</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            1. Abre WhatsApp en tu celular<br />
            2. Ve a Configuración → Dispositivos vinculados<br />
            3. Toca "Vincular un dispositivo"<br />
            4. Escanea este QR:
          </Typography>
          {qrCode && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrCode)}`}
                alt="QR Code"
                style={{ maxWidth: '100%' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Nuevo Período */}
      <Dialog 
        open={periodDialog} 
        onClose={() => {
          setPeriodDialog(false);
          setSelectAll(false);
          setSelectedOwners([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>📅 Notificar Nuevo Período</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Año"
                type="number"
                size="small"
                value={periodData.year}
                onChange={(e) => setPeriodData({ ...periodData, year: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                label="Administrador"
                size="small"
                value={periodData.adminId || ''}
                onChange={(e) => {
                  const selectedOwner = owners.find(o => o.id === parseInt(e.target.value));
                  setPeriodData({ 
                    ...periodData, 
                    adminId: parseInt(e.target.value),
                    admin: selectedOwner ? selectedOwner.name : ''
                  });
                }}
                SelectProps={{ native: true }}
              >
                <option value="">Seleccione...</option>
                {owners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name}
                  </option>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Destinatarios (con WhatsApp)
          </Typography>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Solo propietarios con teléfono registrado: {owners.length}
          </Typography>

          {/* Lista de propietarios */}
          <Box sx={{ 
            maxHeight: 250, 
            overflow: 'auto', 
            border: '1px solid #e0e0e0', 
            borderRadius: 1,
            p: 1.5,
            bgcolor: '#fafafa'
          }}>
            {loadingOwners ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={30} />
              </Box>
            ) : owners.length === 0 ? (
              <Alert severity="warning">
                No hay propietarios con WhatsApp registrado
              </Alert>
            ) : (
              owners.map((owner) => (
                <Box 
                  key={owner.id}
                  sx={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <Checkbox
                    checked={selectedOwners.indexOf(owner.id) !== -1}
                    onChange={() => handleToggleOwner(owner.id)}
                    size="small"
                  />
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {owner.name} - Casa #{owner.house_number}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      📱 {owner.phone}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Box>

          {selectedOwners.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>{selectedOwners.length}</strong> propietario{selectedOwners.length !== 1 ? 's' : ''} seleccionado{selectedOwners.length !== 1 ? 's' : ''}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setPeriodDialog(false);
              setSelectAll(false);
              setSelectedOwners([]);
            }}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleNewPeriod} 
            variant="contained" 
            disabled={loading || !periodData.year || !periodData.adminId || selectedOwners.length === 0}
            startIcon={loading ? <CircularProgress size={20} /> : <WhatsAppIcon />}
          >
            {loading ? 'Enviando...' : `Enviar (${selectedOwners.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Mensaje Personalizado */}
      <Dialog open={customDialog} onClose={() => setCustomDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>💬 Mensaje Personalizado</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Teléfono (ej: 6123-4567)"
            value={customMessage.phone}
            onChange={(e) => setCustomMessage({ ...customMessage, phone: e.target.value })}
            sx={{ mb: 2 }}
            size="small"
            helperText="Formato: 6123-4567 (código de país opcional)"
          />
          <TextField
            fullWidth
            label="Nombre (opcional)"
            value={customMessage.ownerName}
            onChange={(e) => setCustomMessage({ ...customMessage, ownerName: e.target.value })}
            sx={{ mb: 2 }}
            size="small"
          />
          <TextField
            fullWidth
            label="Mensaje"
            multiline
            rows={4}
            value={customMessage.message}
            onChange={(e) => setCustomMessage({ ...customMessage, message: e.target.value })}
            placeholder="Escribe tu mensaje aquí..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomDialog(false)} color="inherit">
            Cancelar
          </Button>
          <Button 
            onClick={handleCustomMessage} 
            variant="contained"
            disabled={loading || !customMessage.phone || !customMessage.message}
            startIcon={loading ? <CircularProgress size={20} /> : <WhatsAppIcon />}
          >
            {loading ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default WhatsAppNotifications;
